import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/AuthContext";
import { TopNav, Footer } from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Solutions from "@/pages/Solutions";
import ProviderProfile from "@/pages/ProviderProfile";
import Compare from "@/pages/Compare";
import RequestQuote from "@/pages/RequestQuote";
import ClaimListing from "@/pages/ClaimListing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ProviderDashboard from "@/pages/ProviderDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProviderEdit from "@/pages/AdminProviderEdit";
import SeoLanding from "@/pages/SeoLanding";
import SubmitProvider from "@/pages/SubmitProvider";
import ListYourCompany from "@/pages/ListYourCompany";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function NotFound() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-32 text-center">
      <div className="font-mono text-xs text-slate-500">404</div>
      <h1 className="font-display font-extrabold text-4xl mt-2">Page not found</h1>
      <p className="mt-3 text-slate-600">The page you're looking for doesn't exist.</p>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <TopNav />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Shell>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/:slug" element={<ProviderProfile />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/request-quotes" element={<RequestQuote />} />
              <Route path="/claim-listing/:slug" element={<ClaimListing />} />
              <Route path="/submit-provider" element={<SubmitProvider />} />
              <Route path="/list-your-company" element={<ListYourCompany />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              <Route path="/provider/dashboard" element={
                <ProtectedRoute role="provider"><ProviderDashboard /></ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>
              } />
              <Route path="/admin/providers/:id/edit" element={
                <ProtectedRoute role="admin"><AdminProviderEdit /></ProtectedRoute>
              } />

              {/* SEO landing pages */}
              <Route path="/tracking-solutions/:country" element={<SeoLanding type="country" />} />
              <Route path="/tracking-categories/:category" element={<SeoLanding type="category" />} />
              <Route path="/tracking-industries/:industry" element={<SeoLanding type="industry" />} />
              <Route path="/gps-tracking-providers/:country" element={<SeoLanding type="country" />} />
              <Route path="/cold-chain-monitoring-providers/:country" element={<SeoLanding type="country" />} />
              <Route path="/asset-tracking-providers/:country" element={<SeoLanding type="country" />} />
              <Route path="/returnable-packaging-tracking" element={<SeoLanding type="static" />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Shell>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
